import { createContext, ReactNode, useEffect, useState } from "react";
import GestautoConst from "../GestautoConst";

type GeralContextProps = {
    children: ReactNode
}

type GeralContextType = {
    token: string,
    userId: number,
    userName: string,
    userCpfCpnj: string,
    userEmail: string,
    userTelefone: string,
    /**
     * [SECURITY] true quando o backend marcou o usuario com
     * force_password_change=1. Enquanto for true, RequireFreshPassword
     * redireciona qualquer rota protegida para /alterar-senha.
     */
    forcePasswordChange: boolean,
    saveUser: (
        userId: number | string,
        userName: string,
        userCpfCpnj: string,
        token: string,
        userEmail?: string,
        userTelefone?: string,
        forcePasswordChange?: boolean,
    ) => void,
    clearUser: () => void,
    /**
     * Apos a troca de senha bem-sucedida em /alterar-senha, marca local
     * que o usuario nao precisa mais trocar. Libera as rotas privadas.
     */
    clearForcePasswordChange: () => void,
}

const initialValues: GeralContextType = {
    token: '',
    userId: 0,
    userName: '',
    userCpfCpnj: '',
    userEmail: '',
    userTelefone: '',
    forcePasswordChange: false,
    saveUser: () => { },
    clearUser: () => { },
    clearForcePasswordChange: () => { },
}

export const GeralContext = createContext<GeralContextType>(initialValues);

export const GeralContextProvider = ({ children }: GeralContextProps) => {
    const [userId, setUserId] = useState(initialValues.userId);
    const [userName, setUserName] = useState(initialValues.userName);
    const [token, setToken] = useState(initialValues.token);
    const [userCpfCpnj, setUserCpfCnpj] = useState(initialValues.userCpfCpnj);
    const [userEmail, setUserEmail] = useState(initialValues.userEmail);
    const [userTelefone, setUserTelefone] = useState(initialValues.userTelefone);
    const [forcePasswordChange, setForcePasswordChange] = useState(initialValues.forcePasswordChange);

    // ============================================================
    // [FIX 2026-05-29] Persistencia trocada de sessionStorage para
    // localStorage com TTL de 24h.
    //
    // sessionStorage era apagado ao fechar a aba/janela do browser,
    // entao o consumidor era deslogado mesmo dentro do periodo de
    // 24h do Passport. Agora usamos localStorage (sobrevive ao close
    // do browser) com um timestamp `__expiresAt`; toda leitura
    // checa se ja passou e limpa se sim.
    //
    // Mantemos uma migracao silenciosa: se houver dados antigos
    // ainda no sessionStorage, copiamos pra localStorage na primeira
    // carga e limpamos o sessionStorage. Isso evita logout forçado
    // nos usuarios que tiverem aberta a aba no momento do deploy.
    // ============================================================
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const EXPIRES_AT_KEY = '__gestauto_expires_at';

    const isExpired = (): boolean => {
        const exp = localStorage.getItem(EXPIRES_AT_KEY);
        if (!exp) return false;
        const t = parseInt(exp, 10);
        if (Number.isNaN(t)) return true;
        return Date.now() > t;
    };

    const renewExpiration = (): void => {
        localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + ONE_DAY_MS));
    };

    const clearAllStorage = (): void => {
        Object.values(GestautoConst.store).forEach((k) => {
            localStorage.removeItem(k);
            sessionStorage.removeItem(k);
        });
        localStorage.removeItem(EXPIRES_AT_KEY);
    };

    useEffect(() => {
        // Migracao silenciosa: copiar dados antigos do sessionStorage
        // pra localStorage se ainda existirem (evita logout no deploy).
        Object.values(GestautoConst.store).forEach((k) => {
            const old = sessionStorage.getItem(k);
            if (old !== null && localStorage.getItem(k) === null) {
                localStorage.setItem(k, old);
            }
        });

        // Se ja passou de 24h desde o ultimo saveUser, limpa tudo
        // e nao restaura sessao.
        if (isExpired()) {
            clearAllStorage();
            return;
        }

        const StorageUserId = localStorage.getItem(GestautoConst.store.userId);
        const StorageUserName = localStorage.getItem(GestautoConst.store.userName);
        const StorageToken = localStorage.getItem(GestautoConst.store.token);
        const StorageUserCpfCnpj = localStorage.getItem(GestautoConst.store.userCpjCnpj);
        const StorageUserEmail = localStorage.getItem(GestautoConst.store.userEmail);
        const StorageUserTelefone = localStorage.getItem(GestautoConst.store.userTelefone);
        const StorageForcePwd = localStorage.getItem(GestautoConst.store.forcePasswordChange);

        setUserId(parseInt(StorageUserId || '0') || 0);
        setUserName(StorageUserName || '');
        setToken(StorageToken || '');
        setUserCpfCnpj(StorageUserCpfCnpj || '');
        setUserEmail(StorageUserEmail || '');
        setUserTelefone(StorageUserTelefone || '');
        setForcePasswordChange(StorageForcePwd === '1');
    }, []);

    const saveUser = (
        id: number | string,
        name: string,
        cpfCpnj: string,
        tk: string,
        email?: string,
        telefone?: string,
        forcePwd?: boolean,
    ) => {
        const numericId = typeof id === 'string' ? parseInt(id) || 0 : id;
        setUserId(numericId);
        setUserName(name);
        setToken(tk);
        setUserCpfCnpj(cpfCpnj);
        if (email !== undefined) setUserEmail(email);
        if (telefone !== undefined) setUserTelefone(telefone);
        if (forcePwd !== undefined) {
            setForcePasswordChange(!!forcePwd);
            localStorage.setItem(GestautoConst.store.forcePasswordChange, forcePwd ? '1' : '0');
        }

        localStorage.setItem(GestautoConst.store.userId, numericId.toString());
        localStorage.setItem(GestautoConst.store.userName, name);
        localStorage.setItem(GestautoConst.store.token, tk);
        localStorage.setItem(GestautoConst.store.userCpjCnpj, cpfCpnj);
        if (email !== undefined) localStorage.setItem(GestautoConst.store.userEmail, email);
        if (telefone !== undefined) localStorage.setItem(GestautoConst.store.userTelefone, telefone);

        // Renova a janela de 24h a cada login/refresh.
        renewExpiration();
    }

    const clearUser = () => {
        setUserId(0);
        setUserName('');
        setToken('');
        setUserCpfCnpj('');
        setUserEmail('');
        setUserTelefone('');
        setForcePasswordChange(false);
        clearAllStorage();
    }

    const clearForcePasswordChange = () => {
        setForcePasswordChange(false);
        localStorage.setItem(GestautoConst.store.forcePasswordChange, '0');
    }

    return (
        <GeralContext.Provider value={{
            userId, userName, userCpfCpnj, token, userEmail, userTelefone,
            forcePasswordChange,
            saveUser, clearUser, clearForcePasswordChange,
        }}>
            {children}
        </GeralContext.Provider>
    )
}
